import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import ModalSubHeader from '@components/ModalComponents/ModalSubHeader'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { BrandGroupProviderModel } from '@models/types'

import useCreateBrandGroupProviderModal from './hooks/useCreateBrandGroupProviderModal'

import SelectBrandGroup from './components/SelectBrandGroup'
import SelectProvider from './components/SelectProvider'

type ModalContextTypes = {
  modalData: {
    brandGroupProvider: Partial<BrandGroupProviderModel>,
  },
  topModalKey: string,
}

type ModalCallbacks = {
  closeModal: () => void,
  createBrandGroupProvider: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateBrandGroupProviderModal = (props: ModalProps) => {
  const { callbacks, modalKey = 'CreateBrandGroupProviderModal', showModal } = props
  const { closeModal, createBrandGroupProvider } = callbacks || {}

  const modalContext = useContext<ModalContextTypes>(ModalContext)
  const { modalData, topModalKey } = modalContext

  const isTopModal = topModalKey === modalKey

  const modalPayload = digObject(modalData, modalKey, {})
  const { brandGroupProvider, brandId } = modalPayload
  const {
    brand_group_id: initBrandGroupId,
    provider_id: initProviderId,
  } = brandGroupProvider

  const createBrandGroupProviderModal = useCreateBrandGroupProviderModal({ brandGroupProvider, brandId, closeModal })

  const {
    brandGroup,
    createBrandGroupProviderPayload,
    creating,
    entityState: {
      identity_code,
    },
    entityText,
    handlers,
    saveEnabled,
  } = createBrandGroupProviderModal

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine={entityText} />

      <ModalSubHeader title="Assign a Provider to" ownerTitle={brandGroup.title} />

      <SidebarModal.Content>
        <Form>
          {(!!initBrandGroupId) && (
            <SelectProvider
              createBrandGroupProviderModal={createBrandGroupProviderModal}
              isTopModal={isTopModal}
            />
          )}

          {!!initProviderId && (
            <SelectBrandGroup
              createBrandGroupProviderModal={createBrandGroupProviderModal}
              isTopModal={isTopModal}
            />
          )}

          <Form.Field label="Identity Code" marginTop="large">
            <input name="identity_code" type="text" value={identity_code} {...handlers} />
          </Form.Field>

        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating}
          onClick={() => createBrandGroupProvider(createBrandGroupProviderPayload)}
          size="large"
        >
          {saveEnabled ? `Add ${entityText}` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateBrandGroupProviderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
